import { useSearchParamsState } from 'hooks/useSearchParamState';
import { useStore } from 'hooks/useStore';
import {
  DropdownDetails,
  OpportunityDetail,
  Stage,
  UpdateBoardPayload,
  updateOpportunity,
} from 'Interfaces/BDPortalInterfaces';
import { DropResult } from 'react-beautiful-dnd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import { deepCopy } from 'utils/deepCopy';

const OpportunityKanbanListLogic = () => {
  const {
    UserStore: { userModulesRoles, userMetadata },
    BDPortalStore: {
      board,
      setBoard,
      filterAnalyst,
      filterAccManagers,
      addInQuickLoad,
      removeFromQuickLoad,
      allOpportunityDetails,
      searchKeyword,
      filterStage,
      filterStatus,
      quickLoadCards,
      getAllOpportunity,
      getDropdownDetails,
      setAllOpportunityDetails,
      filterPrimarySupplier,
      filterSource,
      filterAgency,
      filterIsPortfolioOpportunity,
      filterStartAndEnddates,
      filterAnnualRevenueRange,
    },
  } = useStore();
  const [boardDetails, setBoardDetails] = useState<DropdownDetails | null>(null);
  const [iteration, setIteration] = useState<string>(board);
  const [stageNames, setStageNames] = useState<string[]>([]);
  const [isEditOpportunityModalOpen, setIsEditOpportunityModalOpen] = useState(false);
  const [expandedStageNames, setExpandedStageNames] = useState<string[]>(
    stageNames.length ? [stageNames[0]] : []
  );
  const [opportunityIID, setOpportunityIID] = useState(0);
  const [isDropDownDetailsAndOppLoading, setIsDropDownDetailsAndOppLoading] = useState(false);
  const [quickLoad, _setQuickLoad] = useState(false);
  const [opportunityID, setOpportunityID] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [opportunityIdParam] = useSearchParamsState(
    'opportunityIdParam',
    ''
  );
  const [opportunityIterationIdParam] = useSearchParamsState(
    'opportunityIterationIdParam',
    ''
  );
  const [_quickLoadCardsLocal, _setQuickLoadCardsLocal] = useState<Set<number>>(new Set());

  const [updateBoardPayload, setUpdateBoardPayload] = useState<UpdateBoardPayload>({
    OpportunityIterationsID: null,
    StageID: 1,
    StatusID: 1,
    User: '',
    AnalystID: null,
  });

  useEffect(() => {
    handleGetDropdownDetails();
    // if both the params are in the url open the edit opportunity modal
    if (opportunityIdParam && opportunityIterationIdParam) {
      setOpportunityID(parseInt(opportunityIdParam));
      setOpportunityIID(parseInt(opportunityIterationIdParam));
      setIsEditOpportunityModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDropdownDetails = useCallback(async (signal: AbortSignal | null = null) => {
    setIsDropDownDetailsAndOppLoading(true);
    try {
      await getDropdownDetails(signal || undefined);
      await getAllOpportunity(board, signal || undefined);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('API call was aborted');
      } else {
        console.error(error);
      }
    } finally {
      setIsDropDownDetailsAndOppLoading(false);
    }
  }, [board, getDropdownDetails, getAllOpportunity]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchDropdownDetails(signal);

    return () => {
      controller.abort();
    };
  }, [board, fetchDropdownDetails]);

  useEffect(() => {
    if (stageNames.length > 0) {
      setIteration(board);
      setExpandedStageNames([stageNames[0]]);
    } else {
      setExpandedStageNames([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageNames]);

  const handleGetDropdownDetails = async () => {
    try {
      const response = await getDropdownDetails();
      setBoardDetails(response!);
      if (response) {
        if (board === 'NC' && response.NC) {
          setStageNames(response.NC.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else if (board === 'MAS' && response.MAS) {
          setStageNames(response.MAS.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else {
          setStageNames([]);
        }
      }
    } catch (error) {
      console.error('Error fetching dropdown details:', error);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const [sourceStageId] = source.droppableId.split('|');
    const [destStageId, destStatusId] = destination.droppableId.split('|');

    const movedOpportunity = allOpportunityDetails.find(
      opportunity => opportunity.OpportunityIterationsID === parseInt(draggableId)
    );

    if (!movedOpportunity) return;

    const sourceStage = parseInt(sourceStageId);
    const destStage = parseInt(destStageId);
    const destStatus = parseInt(destStatusId);

    const isValidMove = () => {
      if (sourceStage === destStage) {
        return true;
      }

      if (board === 'NC' || board === 'MAS') {
        if (Math.abs(destStage - sourceStage) === 1) {
          return destStatus === 1;
        }
      }
      return false;
    };

    const originalOpportunities = [...allOpportunityDetails];

    if (isValidMove()) {
      addInQuickLoad(movedOpportunity.OpportunityIterationsID);
      // (movedOpportunity.OpportunityIterationsID);

      try {
        setAllOpportunityDetails(
          allOpportunityDetails.map(opportunity =>
            opportunity.OpportunityIterationsID === movedOpportunity.OpportunityIterationsID
              ? { ...opportunity, StageID: destStage, StatusID: destStatus }
              : opportunity
          )
        );

        const payload: updateOpportunity = {
          OpportunityIterationsID: movedOpportunity.OpportunityIterationsID,
          StageID: destStage,
          StatusID: destStatus,
          AnalystID: movedOpportunity.AnalystID,
        };

        await bdportalScreenServices.updateBoard(payload, board);
      } catch (error: unknown) {
        setAllOpportunityDetails(originalOpportunities);
        removeFromQuickLoad(movedOpportunity.OpportunityIterationsID);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Error updating board: ${errorMessage}`, {
          position: 'bottom-right',
          autoClose: 5000,
        });
      } finally {
        removeFromQuickLoad(movedOpportunity.OpportunityIterationsID);
      }
    } else {
      toast.error(
        `Cards can only move to adjacent status or to the first status of the next stage`,
        {
          position: 'bottom-right',
          autoClose: 5000,
        }
      );
    }
  };

  const openEditOpportunityModal = (boardName: string) => {
    // add parameter in the url logic to be added
    setIteration(boardName);
    setIsEditOpportunityModalOpen(true);
  };

  const closeEditOpportunityModal = (isNewIterationCreated: boolean = false) => {
    setIsEditOpportunityModalOpen(false);
    // if new iteration created fetch the board details again
    if (isNewIterationCreated) {
      fetchDropdownDetails();
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('opportunityIdParam');
    newSearchParams.delete('opportunityIterationIdParam');
    newSearchParams.delete('isIterationOpen');
    setSearchParams(newSearchParams);
  };

  const updateBoard = async (
    StageID?: number,
    StatusID?: number,
    AnalystID?: number,
    opportunityID?: number
  ) => {
    try {
      const payload: updateOpportunity = {
        OpportunityIterationsID: opportunityID!,
        StageID: StageID,
        StatusID: StatusID,
        AnalystID: AnalystID,
      };
      await bdportalScreenServices.updateBoard(payload, board);

      const updatedOpportunities = allOpportunityDetails.map(opp =>
        opp.OpportunityIterationsID === opportunityID
          ? {
              ...opp,
              ...(StageID !== undefined && { StageID }),
              ...(StatusID !== undefined && { StatusID }),
              ...(AnalystID !== undefined && { AnalystID }),
            }
          : opp
      );

      setAllOpportunityDetails(updatedOpportunities);
    } catch (error) {
      console.error('Error updating board:', error);
    }
  };

  const handleSetItertation = (iteration: string) => {
    setIteration(iteration);
    setBoard(iteration);
  };

  const handleListButtonClick = () => {
    // navigation logi
    navigate('bdportal/optracker/list');
  };

  const handleCardClick = async (opportunityIID: number, opportunityID: number) => {
    setIsEditOpportunityModalOpen(true);
    setOpportunityIID(opportunityIID);
    setOpportunityID(opportunityID);
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('opportunityIdParam', opportunityID.toString());
    newSearchParams.set('opportunityIterationIdParam', opportunityIID.toString());
    setSearchParams(newSearchParams);
  };

  const toggleBoardExpansion = (boardName: string) => {
    setExpandedStageNames(prev =>
      prev.includes(boardName) ? prev.filter(name => name !== boardName) : [...prev, boardName]
    );
  };

  // Effect to update state when stageNames change
  useEffect(() => {
    if (stageNames.length > 0) {
      setIteration(board);
      setExpandedStageNames([stageNames[0]]);
    } else {
      setExpandedStageNames([]);
    }
  }, [stageNames, board]);

  const isValidMove = (
    currentStage: number,
    newStage: number,
    _currentStatus: number,
    _newStatus: number,
    board: string
  ): boolean => {
    if (!userMetadata) return false;

    if (
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'developer' ||
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'management'
    ) {
      return true;
    }

    if (userMetadata?.roles.opptracker.length === 0) {
      return false;
    }

    if (
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'admin' ||
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'super'
    ) {
      return true;
    }

    if (currentStage === newStage) {
      return true;
    }

    if (board === 'NC' || board === 'MAS') {
      if (newStage <= 3 && currentStage <= 3) {
        return true;
      }
    }

    return false;
  };

  const handleDropdownChange = async (
    opportunityID: number,
    field: 'Stage' | 'Status' | 'Analyst',
    newValue: number
  ) => {
    const movedOpportunity = allOpportunityDetails.find(
      opportunity => opportunity.OpportunityIterationsID === opportunityID
    );

    if (!movedOpportunity) return;

    const originalOpportunities = [...allOpportunityDetails];
    const currentStage = movedOpportunity.StageID;
    const currentStatus = movedOpportunity.StatusID;
    const currentAnalyst = movedOpportunity.AnalystID;

    const originalValue =
      field === 'Stage' ? currentStage : field === 'Status' ? currentStatus : currentAnalyst;

    if (originalValue === newValue) return;

    if (
      (field === 'Stage' && !isValidMove(currentStage, newValue, currentStatus, 1, board)) ||
      (field === 'Status' &&
        !isValidMove(currentStage, currentStage, currentStatus, newValue, board))
    ) {
      toast.error('Invalid move, requires admin role', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      return;
    }
    addInQuickLoad(opportunityID);

    let tempAllOpportunities = deepCopy(allOpportunityDetails);

    // check if the move from iss pala to uss pala
    if (field === 'Stage') {
      if ((currentStage <= 7 && newValue === 8) || (currentStage === 8 && newValue <= 7)) {
        tempAllOpportunities = tempAllOpportunities.map(opportunity =>
          opportunity.OpportunityIterationsID === opportunityID
            ? {
                ...opportunity,
                StatusID: 1,
              }
            : opportunity
        );
      }
    }

    try {
      // setAllOpportunityDetails(
      tempAllOpportunities = tempAllOpportunities.map(opportunity =>
        opportunity.OpportunityIterationsID === opportunityID
          ? {
              ...opportunity,
              [field === 'Stage' ? 'StageID' : field === 'Status' ? 'StatusID' : 'AnalystID']:
                newValue,
              // ...(field === "Stage" && { StatusID: 1 }),
            }
          : opportunity
      );
      setAllOpportunityDetails(tempAllOpportunities);
      // );

      const payload = {
        OpportunityIterationsID: opportunityID,
        StageID: field === 'Stage' ? newValue : currentStage,
        StatusID: field === 'Status' ? newValue : currentStatus,
        AnalystID: field === 'Analyst' ? newValue : currentAnalyst,
      };

      await bdportalScreenServices.updateBoard(payload, board);
    } catch (error: unknown) {
      setAllOpportunityDetails(originalOpportunities);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error updating ${field}: ${errorMessage}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      removeFromQuickLoad(opportunityID);
    }
  };

  const filterOpportunityIterationsForTheappliedFilters = (
    opportunitIterationsData: OpportunityDetail[]
  ) => {
    let filteredOpportunityIterations: OpportunityDetail[] = [];
    filteredOpportunityIterations = opportunitIterationsData.filter(iterationData => {
      // check if the curent iteratino passed the filtere criteria
      if (
        searchKeyword &&
        !iterationData.MoleculeAndForm.toLocaleLowerCase().includes(
          searchKeyword.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      if (
        !(
          (!filterStage || iterationData.StageID === filterStage) &&
          (!filterStatus || iterationData.StatusID === filterStatus)
        )
      ) {
        return false;
      }

      // check if the opportunity type is portfolio opporunity or not
      if (
        filterIsPortfolioOpportunity !== null &&
        Boolean(iterationData.PortfolioCounter) !== filterIsPortfolioOpportunity
      ) {
        return false;
      }

      if (
        filterAnnualRevenueRange &&
        (filterAnnualRevenueRange[0] || filterAnnualRevenueRange[1])
      ) {
        if (
          !(
            (iterationData.EstAnnualRevenue || 0) >= filterAnnualRevenueRange[0] &&
            (iterationData.EstAnnualRevenue || 0) <= filterAnnualRevenueRange[1]
          )
        ) {
          return false;
        }
      }

      // start end dates range filter

      if (filterStartAndEnddates && filterStartAndEnddates[0] && filterStartAndEnddates[1]) {
        const startDate = new Date(filterStartAndEnddates[0] as Date);
        const endDate = new Date(filterStartAndEnddates[1] as Date);
        const createdAt = new Date(iterationData.CreatedAt);
        if (!(createdAt >= startDate && createdAt <= endDate)) {
          return false;
        }
      }

      // check for the unassigned filter
      if (
        filterAnalyst &&
        filterAnalyst.length > 0 &&
        filterAnalyst.indexOf(iterationData.AnalystID ? iterationData.AnalystID : 0) === -1
      ) {
        return false;
      }

      // check for the account managers filter
      if (
        filterAccManagers &&
        filterAccManagers.length > 0 &&
        filterAccManagers.indexOf(
          iterationData.AccountManagerID ? iterationData.AccountManagerID : 0
        ) === -1
      ) {
        return false;
      }

      // Check if source filter is applied and doesn't match the iteration data
      if (filterSource?.length !== 0 && filterSource?.indexOf(iterationData.SourceID) === -1) {
        return false;
      }

      // Check if primary filter is applied and doesn't match the iteration data
      if (
        filterPrimarySupplier &&
        !(
          iterationData.PrimarySupplierID?.toLocaleLowerCase() ===
          filterPrimarySupplier.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      const agency = iterationData.Agency
        ? JSON.parse(iterationData.Agency.replace(/'/g, '"'))
        : [0];
      if (filterAgency && filterAgency.length > 0) {
        let found = false;
        agency.forEach((agencyInstance: number) => {
          if (filterAgency.indexOf(agencyInstance) !== -1) {
            found = true;
          }
        });
        if (!found) return false;
      }

      return true;
    });
    return filteredOpportunityIterations;
  };

  return {
    userModulesRoles,
    isDropDownDetailsAndOppLoading,
    setBoardDetails,
    boardDetails,
    handleSetItertation,
    iteration,
    handleListButtonClick,
    setOpportunityIID,
    opportunityIID,
    setUpdateBoardPayload,
    updateBoardPayload,
    board,
    filterAnalyst,
    allOpportunityDetails,
    searchKeyword,
    getAllOpportunity,
    getDropdownDetails,
    expandedStageNames,
    stageNames,
    isEditOpportunityModalOpen,
    openEditOpportunityModal,
    closeEditOpportunityModal,
    filterStage,
    onDragEnd,
    toggleBoardExpansion,
    handleCardClick,
    updateBoard,
    handleGetDropdownDetails,
    handleDropdownChange,
    quickLoadCards,
    filterStatus,
    quickLoad,
    opportunityID,
    filterOpportunityIterationsForTheappliedFilters,
  };
};

export default OpportunityKanbanListLogic;
